import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ImageBackground, PixelRatio, Text, Pressable, View, TouchableOpacity } from 'react-native';
import Carousel, { Pagination } from 'react-native-snap-carousel';
import LinearGradient from 'react-native-linear-gradient';
import Theme from 'Theme';
import {
    getAssetImageUrl,
    getDurationString,
    getItemStartTime,
    getItemStartDay,
    isLiveTv,
    getAssetTitle,
    isVod
} from 'Utils';
import {
    DEVICE_ORIENTATION,
    Device,
    getActualWidth,
    addOrientationListener,
    removeOrientationListener,
    getActualHeight
} from 'DeviceUtils';
import {
    getParentalRatingByModel,
    isContentBlocked,
    isContentLocked
} from 'Utils/parentalControlUtils';
import { withStore } from 'Store';
import { t } from 'Locale/index';
import { withConfirmationModal } from 'LocalGluons/components/ConfirmationModal';
import { CachedImage } from 'LocalGluons/components/CachedImage';
import Icon from '../Icon';
import { PROMO_CAROUSEL_MASK, MAX_WEB_WIDTH } from '../../../options';
import defaultStyles from './PromoCarousel.jss';
import CustomCarousel from '../CustomCarousel/CustomCarousel';

const CAROUSEL_INTERVAL = 7000;

class PromoCarousel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeDotIndex: 0
        };

        addOrientationListener(this.onOrientationChange);
    }

    componentWillUnmount() {
        if (!Device.isWeb) clearInterval(this.sliderInterval);
        removeOrientationListener(this.onOrientationChange);
    }

    onCarouselRef = (carousel) => {
        this.carouselRef = carousel;

        if (!carousel) return;
        if (!Device.isWeb) {
            clearInterval(this.sliderInterval);
            this.sliderInterval = setInterval(
                () => this.carouselRef.snapToNext(),
                CAROUSEL_INTERVAL
            );
        }
    };

    onOrientationChange = () => {
        // Rerender on orientation change to scale ui correctly according to new sizes
        this.forceUpdate();
    };

    onSnapToItem = (index) => {
        this.setState({ activeDotIndex: index });
    };

    getCarouselWidth = () => {
        if (Device.isTablet) {
            const windowWidth = getActualWidth();
            const windowHeight = getActualHeight();
            const actualWidth = this.isLandscape() ? windowWidth : windowHeight + 60;
            return actualWidth - Theme.primaryMargin * 2;
        }
        return Device.isWeb
            ? MAX_WEB_WIDTH - Theme.primaryMargin * 2
            : getActualWidth() - Theme.primaryMargin * 2;
    };

    getTabletCarouselHeightByOrientation = () => {
        let percentage = 0.3;
        if (Device.orientation === DEVICE_ORIENTATION.LANDSCAPE) {
            percentage = 0.5;
        }

        const windowWidth = getActualWidth();
        const windowHeight = getActualHeight();
        const actualHeight = this.isLandscape() ? windowHeight : windowWidth - 60;
        const height = PixelRatio.roundToNearestPixel(actualHeight * percentage);
        return height > Theme.minPromoCarouselHeight ? height : Theme.minPromoCarouselHeight;
    };

    getCarouselHeight = () => {
        if (Device.isTablet) return this.getTabletCarouselHeightByOrientation();
        if (Device.isPhone) PixelRatio.roundToNearestPixel(getActualHeight() * 0.3);
        return PixelRatio.roundToNearestPixel(getActualHeight() * 0.25);
    };

    getSubtitleByType = (item) => {
        const { data } = item;

        if (!Object.keys(item.data).length) {
            console.log('Warning: unable to get subtitle for item: ', item);
            return '';
        }

        if (isLiveTv(item))
            return `${data.channelname} | ${getItemStartDay(data)}| ${getItemStartTime(data)}`;

        if (isVod(item)) return getDurationString(item.getDuration(), true);

        return '';
    };

    isLandscape = () => getActualWidth() > getActualHeight();

    renderItem = ({ item }) => {
        const { onItemPress, hideImage, parentalLockConfig, setProtectedContent } = this.props;
        if (item.data && Object.keys(item.data).length > 0) {
            const parentalRating = getParentalRatingByModel(item);
            const adultLock = isContentLocked(item.isAdult(), parentalLockConfig);
            const ageBlock = isContentBlocked(parentalRating, parentalLockConfig);
            const isLocked = adultLock || ageBlock;
            const title = adultLock ? t('COMMON.LOCKED_ADULT_CONTENT') : getAssetTitle(item);
            if (!title) console.log('Warning: unable to get title for item: ', item);

            return (
                <Pressable
                    style={{ width: '100%', height: '100%', flex: 1 }}
                    onPress={() => {
                        if (ageBlock || adultLock) {
                            setProtectedContent(item, () => onItemPress(item));
                        } else {
                            onItemPress(item);
                        }
                    }}
                >
                    {!hideImage && (
                        <>
                            <View style={defaultStyles.imageContainer}>
                                <CachedImage
                                    style={[
                                        {
                                            width: '100%',
                                            height: '100%',
                                            flex: 1
                                        },
                                        adultLock && { backgroundColor: '#333333' }
                                    ]}
                                    source={
                                        adultLock ? '' : { uri: getAssetImageUrl(item, 'PREVIEW') }
                                    }
                                    resizeMode="cover"
                                />
                            </View>

                            {Device.isTabletOrWeb && (
                                <ImageBackground
                                    style={defaultStyles.imageBackground}
                                    source={PROMO_CAROUSEL_MASK}
                                    //defaultSource={PROMO_CAROUSEL_MASK}
                                    resizeMode="cover"
                                />
                            )}

                            <LinearGradient
                                start={{ x: 0, y: Device.isTabletOrWeb ? 0.35 : 0.05 }}
                                end={{ x: 0, y: 1 }}
                                colors={['rgba(0,0,0,0.0)', 'rgba(16,16,16,0.5)']}
                                style={defaultStyles.linearGradient}
                            />
                            <View style={defaultStyles.titlesContainer}>
                                <View style={defaultStyles.titlesContainerTitle}>
                                    <Text style={defaultStyles.title}>
                                        {title && title.toUpperCase()}
                                    </Text>
                                    {isLocked && (
                                        <Icon
                                            name="lock"
                                            style={{ height: 25, width: 25, marginLeft: 8 }}
                                        />
                                    )}
                                </View>
                                <Text style={defaultStyles.subTitle} numberOfLines={1}>
                                    {this.getSubtitleByType(item)}
                                </Text>
                            </View>
                        </>
                    )}
                </Pressable>
            );
        }

        return null;
    };

    render() {
        const { maxItemsLength, style, items } = this.props;
        const { activeDotIndex } = this.state;
        const carouselWidth = this.getCarouselWidth();
        const itemsLength = maxItemsLength || items.length;

        if (!(items && items.length > 0)) {
            return null;
        }

        return (
            <View style={[{ height: this.getCarouselHeight() }, style]} testID="top_promo">
                {Device.isWeb && (
                    <Pressable
                        onPress={() => this.carouselRef.snapToPrev()}
                        style={defaultStyles.leftArrow}
                    >
                        <Icon name="arrow_left" />
                    </Pressable>
                )}
                {Device.isWeb && (
                    <CustomCarousel
                        data={items.slice(0, itemsLength)}
                        renderItem={this.renderItem}
                        onCarouselRef={this.onCarouselRef}
                        carouselWidth={carouselWidth}
                        itemWidth={carouselWidth}
                        activeDot="#fff"
                        inactiveDot="rgba(255, 255, 255, 0.3)"
                    />
                )}
                {!Device.isWeb && (
                    <>
                        <Carousel
                            testID="top_promo_carousel"
                            ref={this.onCarouselRef}
                            data={items.slice(0, itemsLength)}
                            renderItem={this.renderItem}
                            sliderWidth={carouselWidth}
                            inactiveSlideScale={1}
                            inactiveSlideOpacity={1}
                            itemWidth={carouselWidth}
                            containerCustomStyle={defaultStyles.carousel}
                            onSnapToItem={this.onSnapToItem}
                            loop
                        />
                        <Pagination
                            dotsLength={itemsLength}
                            activeDotIndex={activeDotIndex}
                            dotColor="#fff"
                            inactiveDotColor="#fff"
                            inactiveDotOpacity={0.3}
                            inactiveDotScale={1}
                            carouselRef={this.carouselRef}
                            dotContainerStyle={defaultStyles.paginationDotContainer}
                            containerStyle={defaultStyles.paginationContainer}
                        />
                    </>
                )}
                {Device.isWeb && (
                    <Pressable
                        onPress={() => this.carouselRef.snapToNext()}
                        style={defaultStyles.rightArrow}
                    >
                        <Icon name="arrow_right" />
                    </Pressable>
                )}
            </View>
        );
    }
}

PromoCarousel.defaultProps = {
    onItemPress: () => {},
    maxItemsLength: null,
    style: {},
    hideImage: false
};

PromoCarousel.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onItemPress: PropTypes.func,
    maxItemsLength: PropTypes.number,
    style: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.number]),
    hideImage: PropTypes.bool,
    parentalLockConfig: PropTypes.shape({}).isRequired,
    setProtectedContent: PropTypes.func.isRequired
};

const PromoCarouselWithStore = withStore(PromoCarousel);
export default withConfirmationModal(PromoCarouselWithStore);
